"use client";

import Link from "next/link";
import { motion, useReducedMotion } from "framer-motion";
import type { LucideIcon } from "lucide-react";
import {
  ArrowUpRight,
  Check,
  ChevronRight,
  LifeBuoy,
  MessagesSquare,
  Route,
  Wrench,
} from "lucide-react";
import { ConsultationCTA } from "@/components/common";
import { servicesList } from "@/lib/data";
import { t } from "@/content/i18n/messages";
import { defaultLocale, getLocalizedPathname, type Locale } from "@/lib/i18n";

interface ServiceProps {
  locale?: Locale;
}

const serviceRoutes: Record<string, string> = {
  "sds": "/services/software-development/",
  "sis": "/services/system-integration/",
  "dtc": "/services/digital-transformation/",
  "cadi": "/services/cloud-architecture/",
  "wdh": "/services/website-development/",
  "hsp": "/services/hardware-software/",
  "pm": "/services/product-management/",
  "as": "/services/ai-solutions/",
  "aa": "/services/ai-agents/",
  "aiseo": "/services/seo-services-malaysia/",
  "sm": "/services/smart-manufacturing/",
  "te": "/services/training-education/",
};

const AI_SOLUTIONS_ID = "as";

// Hub-only cards: these two live outside the shared `servicesList` (used by
// Footer/Navbar/ServicePageLayout too, each with their own manual links to
// these same routes already) so adding them here can't duplicate entries
// elsewhere. Keyword ownership: "AI agent" -> ai-agents, "AI SEO" -> seo-services-malaysia.
const hubOnlyServices: Array<{
  icon: string;
  title: string;
  description: string;
  detailId: string;
  features: string[];
}> = [
  {
    icon: "fas fa-diagram-project",
    title: "AI Agents",
    description:
      "We build AI agents for Malaysian and Singaporean businesses, automating multi-step workflows on Claude.",
    detailId: "aa",
    features: [
      "Custom AI agent development",
      "Multi-step workflow automation",
      "Built and graded on Claude",
      "Invoice, order and document workflows",
      "Ongoing agent support",
    ],
  },
  {
    icon: "fas fa-magnifying-glass-chart",
    title: "AI SEO",
    description:
      "Get found on Google and in AI answers, with monthly AI visibility tracking for Malaysian businesses.",
    detailId: "aiseo",
    features: [
      "Google and AI Overview visibility",
      "Weekly AI-agent SEO fixes",
      "Monthly AI visibility tracking",
      "Technical SEO audits",
      "ChatGPT and Gemini visibility checks",
    ],
  },
];

// Splits a translated headline on the [[...]] accent marker so translators can
// reorder words freely; the marked phrase gets the emphasis underline.
function renderAccent(text: string) {
  return text.split(/\[\[(.+?)\]\]/g).map((part, index) =>
    index % 2 === 1 ? (
      <span key={index} className="emphasis-underline">
        {part}
      </span>
    ) : (
      part
    ),
  );
}

const processSteps: Array<{
  title: string;
  description: string;
  icon: LucideIcon;
}> = [
  {
    title: "Consultation",
    description: "We understand your needs and challenges",
    icon: MessagesSquare,
  },
  {
    title: "Strategy",
    description: "We develop a tailored approach",
    icon: Route,
  },
  {
    title: "Implementation",
    description: "We execute with precision and care",
    icon: Wrench,
  },
  {
    title: "Support",
    description: "We provide ongoing assistance and optimization",
    icon: LifeBuoy,
  },
];

export default function Service({ locale = defaultLocale }: ServiceProps) {
  const prefersReducedMotion = useReducedMotion();

  // Bento ordering: lead with the emphasized AI Solutions card, then the two
  // hub-only cards (AI Agents, AI SEO) immediately after it.
  const orderedServices = [
    ...servicesList.filter((service) => service.detailId === AI_SOLUTIONS_ID),
    ...hubOnlyServices,
    ...servicesList.filter((service) => service.detailId !== AI_SOLUTIONS_ID),
  ];

  // The first row sits in the first viewport: it moves only, with opacity
  // staying 1 so it paints before hydration (LCP). Later cards still fade.
  const cardReveal = (index: number) => ({
    initial: prefersReducedMotion
      ? false
      : index < 3
        ? { y: 12 }
        : { opacity: 0, y: 28 },
    whileInView: index < 3 ? { y: 0 } : { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-80px" },
    transition: {
      duration: 0.6,
      delay: (index % 3) * 0.1,
      ease: [0.21, 0.6, 0.35, 1] as const,
    },
  });

  return (
    <div className="relative bg-background">
      <div className="relative mx-auto max-w-7xl px-4 pb-24 pt-20 sm:px-6 lg:px-8 lg:pt-28">
        {/* Header */}
        <div className="mb-16 max-w-3xl">
          <p className="mb-3 text-sm font-semibold uppercase tracking-[0.3em] text-neutral-muted">
            {t(locale, "What we deliver")}
          </p>
          <h1 className="font-display text-4xl font-bold leading-[1.12] tracking-tight text-neutral-emphasis md:text-5xl lg:text-6xl">
            {renderAccent(t(locale, "Our [[Services]]"))}
          </h1>
          <p className="mt-5 max-w-2xl text-lg text-neutral-muted">
            {t(locale, "We provide comprehensive technology solutions tailored to your business needs. Our team of experts is committed to delivering high-quality services that drive your digital transformation journey.")}
          </p>
          {/* Contextual anchor to the "Claude partner Malaysia" owner page
              (2026-09-30): body links from high-authority hubs, not badges. */}
          <p className="mt-3 max-w-2xl text-base text-neutral-muted">
            {t(locale, "Our AI work is built on Claude. As a ")}
            <Link
              href={getLocalizedPathname("/partners/anthropic-claude-partner/", locale)}
              className="font-medium text-primary underline underline-offset-4"
            >
              {t(locale, "Claude partner in Malaysia")}
            </Link>
            {t(locale, ", we design, build and run Claude agents in production.")}
          </p>
        </div>

        {/* Bento grid — full cards on sm+; compact list rows on mobile (AI Solutions stays full-size) */}
        <div className="grid grid-cols-1 gap-3 sm:gap-5 md:grid-cols-2 lg:grid-cols-3">
          {orderedServices.map((service, index) => {
            const isEmphasized = service.detailId === AI_SOLUTIONS_ID;
            const href =
              serviceRoutes[service.detailId] ||
              `/service-details#${service.detailId}`;
            const localizedHref = getLocalizedPathname(href, locale);

            return (
              <motion.div
                key={service.detailId}
                {...cardReveal(index)}
                className={`group h-full ${isEmphasized ? "lg:col-span-2" : ""}`}
              >
                {/* Mobile compact row */}
                {!isEmphasized && (
                  <Link
                    href={localizedHref}
                    className="flex items-center gap-4 rounded-2xl border border-border bg-card p-4 transition-colors duration-200 hover:border-neutral-border sm:hidden"
                  >
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10">
                      <i
                        className={`${service.icon} text-lg text-primary`}
                        aria-hidden
                      ></i>
                    </div>
                    <div className="min-w-0 flex-1">
                      <h2 className="font-display text-base font-semibold leading-snug text-neutral-emphasis">
                        {t(locale, service.title)}
                      </h2>
                      <p className="mt-0.5 truncate text-xs text-neutral-muted">
                        {t(locale, service.description)}
                      </p>
                    </div>
                    <ChevronRight
                      className="h-4 w-4 shrink-0 text-neutral-muted"
                      aria-hidden
                    />
                  </Link>
                )}

                {/* Full card */}
                <div
                  className={`${
                    isEmphasized ? "flex" : "hidden sm:flex"
                  } h-full flex-col rounded-2xl border border-border bg-card p-7 transition-colors duration-200 hover:border-neutral-border`}
                >
                  <div className="mb-6 flex items-start justify-between gap-4">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary/10">
                      <i
                        className={`${service.icon} text-xl text-primary`}
                        aria-hidden
                      ></i>
                    </div>
                    {isEmphasized ? (
                      <span className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-xs font-semibold text-primary">
                        <span className="h-2 w-2 rounded-full bg-primary" aria-hidden />
                        {t(locale, "Conversational AI")}
                      </span>
                    ) : (
                      <span className="font-mono text-sm text-neutral-muted/60">
                        {String(index).padStart(2, "0")}
                      </span>
                    )}
                  </div>

                  <h2 className="mb-3 font-display text-xl font-semibold text-neutral-emphasis">
                    {t(locale, service.title)}
                  </h2>
                  <p className="mb-5 text-sm leading-relaxed text-neutral-muted">
                    {t(locale, service.description)}
                  </p>

                  {/* Dark proof moment: mini agent-panel thumbnail */}
                  {isEmphasized && (
                    <div className="mb-6 max-w-sm rounded-2xl bg-hero-background p-5">
                      <div className="mb-3 flex items-center justify-between">
                        <span className="font-mono text-xs text-primary-contrast/50">
                          {t(locale, "Agent session")}
                        </span>
                        <span className="h-1.5 w-1.5 rounded-full bg-primary" aria-hidden />
                      </div>
                      <ul className="space-y-1.5 font-mono text-xs">
                        <li className="text-primary-contrast/70">
                          {t(locale, "Plan — qualify the enquiry")}
                        </li>
                        <li className="text-primary-contrast/70">
                          {t(locale, "Act — draft the proposal")}
                        </li>
                        <li className="text-primary">
                          {t(locale, "Verify — checks passed")}
                        </li>
                      </ul>
                    </div>
                  )}

                  <ul
                    className={`mb-6 flex-1 space-y-2 ${
                      isEmphasized ? "sm:columns-2 sm:gap-6 sm:space-y-0" : ""
                    }`}
                  >
                    {service.features.map((feature, idx) => (
                      <li
                        key={idx}
                        className={`flex items-start gap-2.5 text-sm text-neutral-muted ${
                          isEmphasized ? "sm:mb-2 sm:break-inside-avoid" : ""
                        }`}
                      >
                        <Check
                          className="mt-0.5 h-4 w-4 shrink-0 text-primary"
                          aria-hidden
                        />
                        {t(locale, feature)}
                      </li>
                    ))}
                  </ul>

                  <Link
                    href={localizedHref}
                    className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary transition-all duration-200 group-hover:gap-2.5"
                  >
                    {t(locale, "Learn More")}
                    <ArrowUpRight className="h-4 w-4" aria-hidden />
                  </Link>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Process — full-bleed band */}
      <section className="bg-neutral-surface py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-12 max-w-3xl">
            <p className="mb-3 text-sm font-semibold uppercase tracking-[0.3em] text-neutral-muted">
              {t(locale, "How we work")}
            </p>
            <h2 className="font-display text-3xl font-bold leading-tight tracking-tight text-neutral-emphasis md:text-5xl">
              {renderAccent(t(locale, "Our Service [[Process]]"))}
            </h2>
            <p className="mt-5 max-w-2xl text-lg text-neutral-muted">
              {t(locale, "A disciplined four-step path from first conversation to ongoing support.")}
            </p>
          </div>
          <div className="grid grid-cols-1 gap-5 md:grid-cols-4">
            {processSteps.map(({ title, description, icon: Icon }, index) => (
              <motion.div
                key={title}
                {...cardReveal(index)}
                className="rounded-2xl border border-border bg-card p-7 transition-colors duration-200 hover:border-neutral-border"
              >
                <div className="mb-6 flex items-start justify-between">
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10">
                    <Icon className="h-5 w-5 text-primary" aria-hidden />
                  </div>
                  <span className="font-mono text-sm text-neutral-muted/60">
                    0{index + 1}
                  </span>
                </div>
                <h3 className="mb-2 font-display text-lg font-semibold text-neutral-emphasis">
                  {t(locale, title)}
                </h3>
                <p className="text-sm leading-relaxed text-neutral-muted">
                  {t(locale, description)}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8 lg:pb-28">
        <ConsultationCTA
          title={t(locale, "Ready to get started?")}
          description={t(locale, "Explore our comprehensive range of technology services designed to help your business thrive in the digital age. Let's discuss how we can support your goals.")}
          buttonText={t(locale, "Meet with Us")}
        />
      </div>
    </div>
  );
}
